import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "../../data/content";
import { scrollToId } from "../../lib/lenis";

export const LogoMark = ({ size = 36 }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" data-testid="logo-mark" aria-hidden="true">
    <rect width="64" height="64" rx="14" fill="#0E0C0A" stroke="#2E241E" />
    <circle cx="32" cy="32" r="20" fill="#C85A17" />
    <path d="M27 23l13 9-13 9z" fill="#0E0C0A" />
  </svg>
);

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (id === "get-featured") {
      navigate("/get-featured");
      return;
    }
    if (pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 250);
    } else {
      scrollToId(id);
    }
  };

  return (
    <>
      <header
        data-testid="main-nav"
        className={`fixed top-0 left-0 right-0 z-[80] transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled ? "bg-obsidian/85 backdrop-blur-xl border-b border-woodborder" : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-[72px] flex items-center justify-between">
          <button onClick={() => go("top")} className="flex items-center gap-3 group" data-testid="nav-logo">
            <LogoMark />
            <span className="font-display text-xl tracking-wide text-cream leading-none pt-1">
              THE <span className="text-burnt group-hover:text-burnt-hover transition-colors">COBAIN</span> REPORT
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-8" data-testid="nav-links-desktop">
            {NAV_LINKS.slice(0, 5).map((l) => (
              <button
                key={l.id}
                onClick={() => go(l.id)}
                data-testid={`nav-link-${l.id}`}
                className="font-mono text-[11px] tracking-[0.22em] text-sand hover:text-cream transition-colors"
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={() => go("get-featured")}
              data-testid="nav-cta-get-featured"
              className="font-mono text-[11px] tracking-[0.22em] bg-burnt hover:bg-burnt-hover text-cream px-5 py-2.5 rounded-full transition-colors"
            >
              GET FEATURED
            </button>
          </nav>

          <button
            className="lg:hidden text-cream p-2"
            onClick={() => setOpen(true)}
            data-testid="nav-menu-open"
            aria-label="Open menu"
          >
            <Menu size={26} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] bg-obsidian/97 backdrop-blur-2xl flex flex-col"
            data-testid="nav-mobile-menu"
          >
            <div className="flex items-center justify-between px-5 h-[72px]">
              <div className="flex items-center gap-3">
                <LogoMark />
                <span className="font-display text-xl tracking-wide text-cream pt-1">THE COBAIN REPORT</span>
              </div>
              <button onClick={() => setOpen(false)} className="text-cream p-2" data-testid="nav-menu-close" aria-label="Close menu">
                <X size={28} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-1">
              {NAV_LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.4 }}
                  onClick={() => go(l.id)}
                  data-testid={`nav-mobile-link-${l.id}`}
                  className="text-left font-display text-5xl sm:text-6xl tracking-wide text-cream hover:text-burnt transition-colors py-1"
                >
                  <span className="font-mono text-xs text-burnt align-super mr-3">0{i + 1}</span>
                  {l.label}
                </motion.button>
              ))}
              <motion.button
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4, duration: 0.4 }}
                onClick={() => go("partner")}
                data-testid="nav-mobile-link-partner"
                className="text-left font-mono text-sm tracking-[0.25em] text-tan hover:text-gold-bright transition-colors mt-6"
              >
                PARTNER WITH COBAIN →
              </motion.button>
            </nav>
            <p className="px-8 pb-8 font-mono text-[10px] tracking-[0.3em] text-clay">DETROIT, MI — FOR THE CULTURE</p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
